import type { ComponentType } from 'react';

import type { RouteObject } from 'react-router-dom';
import { Navigate, createBrowserRouter, useParams } from 'react-router-dom';

import AdminRouteLayout from '@/features/board/AdminRouteLayout';
import AdminRootLayout from '@/pages/AdminRootLayout/AdminRootLayout';
import RootLayout from '@/pages/RootLayout/RootLayout';
import RouteErrorPage from '@/pages/RouteErrorPage/RouteErrorPage';
import type { CatalogCategorySlug } from '@/routes/routeRegistry';
import { routePaths } from '@/routes/routeRegistry';

interface LazyPageModule {
  default: ComponentType;
}

interface LazyCatalogCategoryModule {
  default: ComponentType<{ categorySlug: string }>;
}

const createLazyRoute = (importer: () => Promise<LazyPageModule>) => {
  return async () => {
    const { default: Component } = await importer();
    return { Component };
  };
};

const createLazyCatalogCategoryRoute = (categorySlug: CatalogCategorySlug) => {
  return async () => {
    const { default: Component } =
      (await import('@/pages/CatalogCategoryPage/CatalogCategoryPage')) as LazyCatalogCategoryModule;

    const CatalogCategoryRoute = () => {
      return <Component categorySlug={categorySlug} />;
    };

    CatalogCategoryRoute.displayName = `CatalogCategoryRoute(${categorySlug})`;

    return { Component: CatalogCategoryRoute };
  };
};

const createLazyDynamicCatalogCategoryRoute = () => {
  return async () => {
    const { default: Component } =
      (await import('@/pages/CatalogCategoryPage/CatalogCategoryPage')) as LazyCatalogCategoryModule;

    const CatalogCategoryRoute = () => {
      const params = useParams();

      return <Component categorySlug={params['categorySlug'] ?? ''} />;
    };

    CatalogCategoryRoute.displayName = 'CatalogCategoryRoute(dynamic)';

    return { Component: CatalogCategoryRoute };
  };
};

const adminRoutes: RouteObject[] = [
  {
    path: '/admin',
    element: <AdminRootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        path: 'login',
        lazy: createLazyRoute(() => import('@/pages/AdminLoginPage/AdminLoginPage')),
      },
      {
        element: <AdminRouteLayout />,
        children: [
          {
            index: true,
            element: <Navigate replace to={routePaths.adminInquiries} />,
          },
          {
            path: 'inquiries',
            lazy: createLazyRoute(
              () => import('@/pages/AdminInquiryListPage/AdminInquiryListPage'),
            ),
          },
          {
            path: 'inquiries/:inquiryId',
            lazy: createLazyRoute(
              () => import('@/pages/AdminInquiryDetailPage/AdminInquiryDetailPage'),
            ),
          },
          {
            path: 'history-entries',
            lazy: createLazyRoute(
              () => import('@/pages/AdminHistoryEntryListPage/AdminHistoryEntryListPage'),
            ),
          },
          {
            path: 'history-entries/new',
            lazy: createLazyRoute(
              () => import('@/pages/AdminHistoryEntryFormPage/AdminHistoryEntryFormPage'),
            ),
          },
          {
            path: 'history-entries/:historyEntryId/edit',
            lazy: createLazyRoute(
              () => import('@/pages/AdminHistoryEntryFormPage/AdminHistoryEntryFormPage'),
            ),
          },
          {
            path: 'organization',
            lazy: createLazyRoute(
              () => import('@/pages/AdminOrganizationPage/AdminOrganizationPage'),
            ),
          },
          {
            path: 'home-hero',
            lazy: createLazyRoute(
              () => import('@/pages/AdminHomeHeroTextPage/AdminHomeHeroTextPage'),
            ),
          },
          {
            path: 'home-featured-equipments',
            lazy: createLazyRoute(
              () =>
                import('@/pages/AdminHomeFeaturedEquipmentListPage/AdminHomeFeaturedEquipmentListPage'),
            ),
          },
          {
            path: 'home-featured-equipments/new',
            lazy: createLazyRoute(
              () =>
                import('@/pages/AdminHomeFeaturedEquipmentFormPage/AdminHomeFeaturedEquipmentFormPage'),
            ),
          },
          {
            path: 'home-featured-equipments/:homeFeaturedEquipmentId/edit',
            lazy: createLazyRoute(
              () =>
                import('@/pages/AdminHomeFeaturedEquipmentFormPage/AdminHomeFeaturedEquipmentFormPage'),
            ),
          },
          {
            path: 'media-coverages',
            lazy: createLazyRoute(
              () => import('@/pages/AdminMediaCoverageListPage/AdminMediaCoverageListPage'),
            ),
          },
          {
            path: 'media-coverages/new',
            lazy: createLazyRoute(
              () => import('@/pages/AdminMediaCoverageFormPage/AdminMediaCoverageFormPage'),
            ),
          },
          {
            path: 'media-coverages/:mediaCoverageId/edit',
            lazy: createLazyRoute(
              () => import('@/pages/AdminMediaCoverageFormPage/AdminMediaCoverageFormPage'),
            ),
          },
          {
            path: 'certification-assets',
            lazy: createLazyRoute(
              () =>
                import('@/pages/AdminCertificationAssetListPage/AdminCertificationAssetListPage'),
            ),
          },
          {
            path: 'certification-assets/new',
            lazy: createLazyRoute(
              () =>
                import('@/pages/AdminCertificationAssetFormPage/AdminCertificationAssetFormPage'),
            ),
          },
          {
            path: 'certification-assets/:certificationAssetId/edit',
            lazy: createLazyRoute(
              () =>
                import('@/pages/AdminCertificationAssetFormPage/AdminCertificationAssetFormPage'),
            ),
          },
          {
            path: 'catalog-items',
            lazy: createLazyRoute(
              () => import('@/pages/AdminCatalogItemHubPage/AdminCatalogItemHubPage'),
            ),
          },
          {
            path: 'catalog-categories',
            lazy: createLazyRoute(
              () => import('@/pages/AdminCatalogCategoryPage/AdminCatalogCategoryPage'),
            ),
          },
          {
            path: 'catalog-items/:categorySlug',
            lazy: createLazyRoute(
              () => import('@/pages/AdminCatalogItemListPage/AdminCatalogItemListPage'),
            ),
          },
          {
            path: 'catalog-items/:categorySlug/new',
            lazy: createLazyRoute(
              () => import('@/pages/AdminCatalogItemFormPage/AdminCatalogItemFormPage'),
            ),
          },
          {
            path: 'catalog-items/:categorySlug/:catalogItemId/edit',
            lazy: createLazyRoute(
              () => import('@/pages/AdminCatalogItemFormPage/AdminCatalogItemFormPage'),
            ),
          },
          {
            path: 'popups',
            lazy: createLazyRoute(() => import('@/pages/AdminPopupListPage/AdminPopupListPage')),
          },
          {
            path: 'popups/new',
            lazy: createLazyRoute(() => import('@/pages/AdminPopupFormPage/AdminPopupFormPage')),
          },
          {
            path: 'popups/:popupId/edit',
            lazy: createLazyRoute(() => import('@/pages/AdminPopupFormPage/AdminPopupFormPage')),
          },
          {
            path: 'promo-videos',
            lazy: createLazyRoute(
              () => import('@/pages/AdminPromoVideoListPage/AdminPromoVideoListPage'),
            ),
          },
          {
            path: 'promo-videos/new',
            lazy: createLazyRoute(
              () => import('@/pages/AdminPromoVideoFormPage/AdminPromoVideoFormPage'),
            ),
          },
          {
            path: 'promo-videos/:promoVideoId/edit',
            lazy: createLazyRoute(
              () => import('@/pages/AdminPromoVideoFormPage/AdminPromoVideoFormPage'),
            ),
          },
          {
            path: 'construction-cases',
            lazy: createLazyRoute(
              () => import('@/pages/AdminConstructionCaseListPage/AdminConstructionCaseListPage'),
            ),
          },
          {
            path: 'construction-cases/new',
            lazy: createLazyRoute(
              () => import('@/pages/AdminConstructionCaseFormPage/AdminConstructionCaseFormPage'),
            ),
          },
          {
            path: 'construction-cases/:constructionCaseId/edit',
            lazy: createLazyRoute(
              () => import('@/pages/AdminConstructionCaseFormPage/AdminConstructionCaseFormPage'),
            ),
          },
          {
            path: 'project-records',
            lazy: createLazyRoute(
              () => import('@/pages/AdminProjectRecordListPage/AdminProjectRecordListPage'),
            ),
          },
          {
            path: 'project-records/new',
            lazy: createLazyRoute(
              () => import('@/pages/AdminProjectRecordFormPage/AdminProjectRecordFormPage'),
            ),
          },
          {
            path: 'project-records/:projectRecordId/edit',
            lazy: createLazyRoute(
              () => import('@/pages/AdminProjectRecordFormPage/AdminProjectRecordFormPage'),
            ),
          },
          {
            path: 'support-materials',
            lazy: createLazyRoute(
              () => import('@/pages/AdminSupportMaterialListPage/AdminSupportMaterialListPage'),
            ),
          },
          {
            path: 'support-materials/new',
            lazy: createLazyRoute(
              () => import('@/pages/AdminSupportMaterialFormPage/AdminSupportMaterialFormPage'),
            ),
          },
          {
            path: 'support-materials/:supportMaterialId/edit',
            lazy: createLazyRoute(
              () => import('@/pages/AdminSupportMaterialFormPage/AdminSupportMaterialFormPage'),
            ),
          },
          {
            path: 'web-catalogs',
            lazy: createLazyRoute(
              () => import('@/pages/AdminWebCatalogListPage/AdminWebCatalogListPage'),
            ),
          },
          {
            path: 'web-catalogs/new',
            lazy: createLazyRoute(
              () => import('@/pages/AdminWebCatalogFormPage/AdminWebCatalogFormPage'),
            ),
          },
          {
            path: 'web-catalogs/:webCatalogId/edit',
            lazy: createLazyRoute(
              () => import('@/pages/AdminWebCatalogFormPage/AdminWebCatalogFormPage'),
            ),
          },
        ],
      },
    ],
  },
];

const publicRoutes: RouteObject[] = [
  {
    index: true,
    lazy: createLazyRoute(() => import('@/pages/HomePage/HomePage')),
  },
  {
    path: 'about/greeting',
    lazy: createLazyRoute(() => import('@/pages/AboutGreetingPage/AboutGreetingPage')),
  },
  {
    path: 'about/history',
    lazy: createLazyRoute(() => import('@/features/aboutHistory/AboutHistory')),
  },
  {
    path: 'about/certification',
    lazy: createLazyRoute(() => import('@/pages/AboutCertificationPage/AboutCertificationPage')),
  },
  {
    path: 'about/organization',
    lazy: createLazyRoute(() => import('@/pages/AboutOrganizationPage/AboutOrganizationPage')),
  },
  {
    path: 'about/location',
    lazy: createLazyRoute(() => import('@/pages/AboutLocationPage/AboutLocationPage')),
  },
  {
    path: routePaths.energySolution.slice(1),
    lazy: createLazyCatalogCategoryRoute('energy-solution'),
  },
  {
    path: routePaths.mechanicalHvac.slice(1),
    lazy: createLazyCatalogCategoryRoute('mechanical-hvac'),
  },
  {
    path: routePaths.refrigerationSystem.slice(1),
    lazy: createLazyCatalogCategoryRoute('refrigeration-system'),
  },
  {
    path: routePaths.promotionalVideos.slice(1),
    lazy: createLazyRoute(
      () => import('@/pages/PromotionalVideoListPage/PromotionalVideoListPage'),
    ),
  },
  {
    path: `${routePaths.promotionalVideos.slice(1)}/:promotionalVideoSlug`,
    lazy: createLazyRoute(
      () => import('@/pages/PromotionalVideoDetailPage/PromotionalVideoDetailPage'),
    ),
  },
  {
    path: routePaths.constructionCases.slice(1),
    lazy: createLazyRoute(
      () => import('@/pages/ConstructionCaseListPage/ConstructionCaseListPage'),
    ),
  },
  {
    path: routePaths.mediaCoverages.slice(1),
    lazy: createLazyRoute(() => import('@/pages/MediaCoverageListPage/MediaCoverageListPage')),
  },
  {
    path: `${routePaths.constructionCases.slice(1)}/:constructionCaseSlug`,
    lazy: createLazyRoute(
      () => import('@/pages/ConstructionCaseDetailPage/ConstructionCaseDetailPage'),
    ),
  },
  {
    path: routePaths.projectRecords.slice(1),
    lazy: createLazyRoute(() => import('@/pages/ProjectRecordListPage/ProjectRecordListPage')),
  },
  {
    path: routePaths.supportMaterials.slice(1),
    lazy: createLazyRoute(() => import('@/pages/SupportMaterialListPage/SupportMaterialListPage')),
  },
  {
    path: `${routePaths.supportMaterials.slice(1)}/:supportMaterialSlug`,
    lazy: createLazyRoute(
      () => import('@/pages/SupportMaterialDetailPage/SupportMaterialDetailPage'),
    ),
  },
  {
    path: routePaths.webCatalogs.slice(1),
    lazy: createLazyRoute(() => import('@/pages/WebCatalogListPage/WebCatalogListPage')),
  },
  {
    path: `${routePaths.webCatalogs.slice(1)}/:webCatalogSlug`,
    lazy: createLazyRoute(() => import('@/pages/WebCatalogDetailPage/WebCatalogDetailPage')),
  },
  {
    path: 'catalog-items/:itemSlug',
    lazy: createLazyRoute(() => import('@/pages/CatalogDetailPage/CatalogDetailPage')),
  },
  {
    path: ':categorySlug',
    lazy: createLazyDynamicCatalogCategoryRoute(),
  },
  {
    path: '*',
    lazy: createLazyRoute(() => import('@/pages/NotFoundPage/NotFoundPage')),
  },
];

const publicRouteShells: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: publicRoutes,
  },
  {
    path: '/en',
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: publicRoutes,
  },
];

export const appRoutes: RouteObject[] = [...adminRoutes, ...publicRouteShells];

export const router = createBrowserRouter(appRoutes);
